import { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeAudio } from '../services/voice-api';
import type { TranscriptionResponse } from '../services/voice-api';

export type VoiceRecorderState = 'idle' | 'requesting' | 'recording' | 'transcribing' | 'error';

export interface UseVoiceRecorderOptions {
  maxDurationMs?: number;
  onTranscriptReady: (result: TranscriptionResponse) => void;
  onError?: (error: string) => void;
}

export interface UseVoiceRecorderReturn {
  state: VoiceRecorderState;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  analyserNode: AnalyserNode | null;
  duration: number;
  error: string | null;
}

function getPreferredMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

const DEFAULT_MAX_DURATION_MS = 120_000; // 2 minutes

export function useVoiceRecorder(options: UseVoiceRecorderOptions): UseVoiceRecorderReturn {
  const { maxDurationMs = DEFAULT_MAX_DURATION_MS, onTranscriptReady, onError } = options;

  const [state, setState] = useState<VoiceRecorderState>('idle');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const mimeTypeRef = useRef<string>('');

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setAnalyserNode(null);
    setDuration(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  const handleRecordingComplete = useCallback(
    async (chunks: Blob[], mime: string) => {
      if (chunks.length === 0) {
        setState('idle');
        return;
      }

      setState('transcribing');

      try {
        const audioBlob = new Blob(chunks, { type: mime });
        const result = await transcribeAudio(audioBlob);
        setState('idle');
        onTranscriptReady(result);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Transcription failed';
        setError(msg);
        setState('error');
        onError?.(msg);
        // Auto-recover to idle after brief error display
        setTimeout(() => setState('idle'), 3000);
      }
    },
    [onTranscriptReady, onError],
  );

  const startRecording = useCallback(async () => {
    setError(null);
    setState('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getPreferredMimeType();
      mimeTypeRef.current = mimeType;

      // Set up Web Audio analyser for waveform visualization
      const audioContext = new AudioContext();
      // iOS requires resume from user gesture
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      setAnalyserNode(analyser);

      // Set up MediaRecorder
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const chunks = [...audioChunksRef.current];
        const mime = mimeTypeRef.current;
        cleanup();
        handleRecordingComplete(chunks, mime);
      };

      recorder.start(250); // collect in 250ms chunks
      startTimeRef.current = Date.now();
      setState('recording');

      // Duration timer
      timerRef.current = setInterval(() => {
        setDuration(Date.now() - startTimeRef.current);
      }, 200);

      // Auto-stop at max duration
      maxTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, maxDurationMs);
    } catch (err) {
      cleanup();
      const msg =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access denied'
          : err instanceof Error
            ? err.message
            : 'Failed to start recording';
      setError(msg);
      setState('error');
      onError?.(msg);
      setTimeout(() => setState('idle'), 3000);
    }
  }, [maxDurationMs, cleanup, handleRecordingComplete, onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop(); // triggers onstop → handleRecordingComplete
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      // Prevent onstop from triggering transcription
      mediaRecorderRef.current.onstop = null;
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }
    cleanup();
    setState('idle');
  }, [cleanup]);

  return {
    state,
    startRecording,
    stopRecording,
    cancelRecording,
    analyserNode,
    duration,
    error,
  };
}
